import React, { useEffect, useMemo, useState } from 'react';

import clsx from 'clsx';
import { PanelTopHeader } from '@/components/panels/PanelTopHeader/PanelTopHeader';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useEditor, type SerializedNodes } from '@craftjs/core';
import { useCraftHelper } from '@/hooks/useCraftHelper';
import { SectionFieldSaved } from '../SectionFieldSaved/SectionFieldSaved';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  createSection,
  deleteSectionById,
  fetchSections,
  updateSectionById,
} from '@/store/section/thunks';
import { selectSectionList } from '@/store/section/selectors';
import { selectUserAccessId } from '@/store/user/selectors';
import { ASSETS_PANEL, normalizeSectionGroupId } from '@builder4/shared/utils/constants';

import { SaveSectionFilters } from '@/components/panels/sections/SaveSectionFilters/SaveSectionFilters';
import { useMixpanel } from '@/hooks/useMixpanel';
import { EVENT, SOURCE, PROPERTY } from '@/analytics/mixpanel';
import { clearFormIdentifiersFromSerializedNodes } from '@/utils/formDuplicationUtils';
import { parseOwnerIds } from '@/utils/sectionLibrary';

interface SectionProps {
  className: string;
  onSectionClick?: (sectionName: string) => void;
  onHidePanel: (value: boolean) => void;
}

export const SectionSaved: React.FC<SectionProps> = ({
  className,
  onSectionClick,
  onHidePanel,
}) => {
  const { getSectionNodeTreeWithUniqueIds, addSectionToRoot } = useCraftHelper();
  const dispatch = useAppDispatch();
  const track = useMixpanel();

  const publishOwnerIds = useMemo(
    () => parseOwnerIds(import.meta.env.VITE_SECTION_LIBRARY_OWNER_ID),
    [],
  );
  const publishAllowlistEnabled = publishOwnerIds.length > 0;

  const sections = useAppSelector(selectSectionList);
  const accessId = useAppSelector(selectUserAccessId);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const { connectors, actions, selected } = useEditor((_, query) => ({
    selected: query.getEvent('selected').first(),
  }));

  const handleClick = (sectionName: string) => {
    if (onSectionClick) {
      onSectionClick(sectionName);
    }
  };

  useEffect(() => {
    dispatch(fetchSections());
  }, [dispatch]);

  const savedSections = useMemo(
    () => (accessId ? sections.filter((section) => section.ownerId === accessId) : []),
    [accessId, sections],
  );
  const filteredSections = useMemo(() => {
    const normalizedSearch = searchTerm.toLowerCase();
    return savedSections.filter(({ name, sectionGroupId }) => {
      const matchesSearch = name?.toLowerCase().includes(normalizedSearch) ?? false;
      const matchesCategory =
        selectedCategoryIds.length === 0 ||
        selectedCategoryIds.includes(normalizeSectionGroupId(sectionGroupId));
      return matchesSearch && matchesCategory;
    });
  }, [savedSections, searchTerm, selectedCategoryIds]);

  const handleSectionSelect = (sectionData: { rootNodeId: string; nodes: SerializedNodes }) => {
    const newTree = getSectionNodeTreeWithUniqueIds(sectionData);
    addSectionToRoot(newTree, selected ?? null);
  };

  const handleOnDelete = (rootNodeId: string) => {
    dispatch(deleteSectionById(rootNodeId));
  };

  const handleOnDuplicate = (id: string) => {
    const section = savedSections.find((section) => section.id === id);
    if (!section) return;
    const { name, craftJson, sectionGroupId } = section;
    const duplicateName = `${name} (Copy)`;

    // Clear unique form and field identifiers from the duplicated section
    const clearedCraftJson = {
      ...craftJson,
      nodes: clearFormIdentifiersFromSerializedNodes(craftJson.nodes),
    };

    dispatch(
      createSection({
        name: duplicateName,
        section_group_id: sectionGroupId,
        craft_json: clearedCraftJson,
        showDuplicateSuccessToast: true,
      }),
    );

    track(EVENT.SECTION_SAVED, {
      [PROPERTY.SOURCE]: SOURCE.SIDEBAR,
    });
  };

  const handleOnRename = (
    id: string,
    newName: string,
    craftJson: {
      rootNodeId: string;
      nodes: SerializedNodes;
    },
    sectionGroupId: string | undefined,
  ) => {
    dispatch(
      updateSectionById({
        sectionId: id,
        body: {
          name: newName,
          craftJson,
          sectionGroupId,
        },
      }),
    );
  };

  const handleOnPublish = (
    id: string,
    name: string,
    craftJson: {
      rootNodeId: string;
      nodes: SerializedNodes;
    },
    sectionGroupId: string | undefined,
  ) => {
    dispatch(
      updateSectionById({
        sectionId: id,
        body: {
          name,
          craftJson,
          sectionGroupId,
          published: true,
        },
      }),
    );
  };

  const handleOnUnpublish = (
    id: string,
    name: string,
    craftJson: {
      rootNodeId: string;
      nodes: SerializedNodes;
    },
    sectionGroupId: string | undefined,
  ) => {
    dispatch(
      updateSectionById({
        sectionId: id,
        body: {
          name,
          craftJson,
          sectionGroupId,
          published: false,
        },
      }),
    );
  };

  return (
    <div className={clsx(className, 'flex h-full max-w-[280px] flex-col')}>
      <PanelTopHeader
        className=""
        text={'Saved Section'}
        backButton={true}
        onClickClose={() => handleClick('')}
        onClickBtnBack={() => handleClick(ASSETS_PANEL.SECTIONS)}
      />
      <div className="p-6">
        <SaveSectionFilters
          className=""
          defaultOpen={false}
          search={searchTerm}
          onSearchChange={(val: string) => setSearchTerm(val)}
          savedSections={savedSections}
          onApply={(activeIds: string[]) => {
            setSelectedCategoryIds(activeIds);
          }}
        />
      </div>

      <ScrollArea className="flex w-full flex-1 gap-1 overflow-hidden px-6">
        <div className="flex flex-col gap-3">
          {filteredSections.length === 0 ? (
            <p className="text-center text-neutral-500">No sections found</p>
          ) : (
            filteredSections.map(({ id, name, craftJson, sectionGroupId, ownerId, published }) => {
              const sectionName = name || 'Untitled';
              const canPublishThisSection =
                publishAllowlistEnabled && publishOwnerIds.includes(ownerId);
              const canTogglePublish = ownerId === accessId && canPublishThisSection;
              return (
                <SectionFieldSaved
                  title={sectionName}
                  onDragStart={() => {
                    onHidePanel(true);
                  }}
                  onDragEnd={() => {
                    onHidePanel(false);
                  }}
                  key={id}
                  sectionData={craftJson}
                  showPublishedChip={ownerId === accessId && published}
                  onDelete={() => handleOnDelete(id)}
                  onDuplicate={() => handleOnDuplicate(id)}
                  onRename={(newName) => handleOnRename(id, newName, craftJson, sectionGroupId)}
                  onPublish={
                    canTogglePublish && published !== true
                      ? () => handleOnPublish(id, sectionName, craftJson, sectionGroupId)
                      : undefined
                  }
                  onUnpublish={
                    canTogglePublish && published === true
                      ? () => handleOnUnpublish(id, sectionName, craftJson, sectionGroupId)
                      : undefined
                  }
                  onClick={() => handleSectionSelect(craftJson)}
                  ref={(ref) => {
                    if (ref) {
                      connectors.create(ref, () => getSectionNodeTreeWithUniqueIds(craftJson), {
                        onCreate: (nodeTree) => {
                          actions.selectNode(nodeTree.rootNodeId);
                        },
                      });
                    }
                  }}
                />
              );
            })
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
